import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'
import { useCategories } from '../context/CategoriesContext'
import { useLanguage } from '../context/LanguageContext'
import { getTodayDate } from '../lib/utils'
import { formatDuration } from '../utils/durationUtils'
import { Button } from '../components/ui/button'
import { Progress } from '../components/ui/progress'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '../components/ui/drawer'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Textarea } from '../components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { toast } from 'sonner'
import { 
  Dumbbell, Brain, Laptop, CandlestickChart, TrendingUp, 
  Building2, BookOpen, Check, History, Search, Heart, Star,
  Target, Zap, Coffee, Music, Palette, Briefcase, GraduationCap,
  Users, Home, Car, Plane, Circle, Pencil, List
} from 'lucide-react'
import { useMediaQuery } from '../hooks/useMediaQuery'
import BonsaiCard from '../components/BonsaiCard'
import RemindersWidget from '../components/RemindersWidget'
import KaizenWidget from '../components/KaizenWidget'
import StreakCelebrationOverlay from '../components/StreakCelebrationOverlay'
import { 
  QuickAddMejoraCategoryButton, 
  QuickAddMejoraSubcategoryButton 
} from '../components/modals/MejoraCategoryModal'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

// Icon mapping
const iconMap = {
  Dumbbell, Brain, Laptop, CandlestickChart, TrendingUp, 
  Building2, BookOpen, Heart, Star, Target, Zap, Coffee, 
  Music, Palette, Briefcase, GraduationCap, Users, Home, Car, Plane, Circle
}

export default function HoyPage() {
  const { user } = useAuth()
  const { activeCategories, loading: categoriesLoading, getCategoryById, fetchCategories } = useCategories()
  const { t, locale, formatDate, formatNumber } = useLanguage()
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [editingLog, setEditingLog] = useState(null) // For editing existing logs
  const [showModal, setShowModal] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [historyCategory, setHistoryCategory] = useState(null)
  const [categoryHistory, setCategoryHistory] = useState([])
  const [historyFilter, setHistoryFilter] = useState('all') // 'today', 'week', 'month', 'all'
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  
  // Streak state
  const [showStreakCelebration, setShowStreakCelebration] = useState(false)
  const [celebrationStreak, setCelebrationStreak] = useState(0)
  
  const isDesktop = useMediaQuery('(min-width: 768px)')
  const today = getTodayDate()
  const [migrationDone, setMigrationDone] = useState(false)

  // One-time migration from points to duration
  useEffect(() => {
    const runMigration = async () => {
      try {
        const statusRes = await axios.get(`${API}/logs/migration-status`)
        if (!statusRes.data.migrated && statusRes.data.logs_needing_migration > 0) {
          await axios.post(`${API}/logs/migrate-to-duration`)
          console.log('Migration completed')
        }
        setMigrationDone(true)
      } catch (error) {
        console.error('Migration check failed:', error)
        setMigrationDone(true) // Continue anyway
      }
    }
    runMigration()
  }, [])

  const fetchTodayLogs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/logs`, { params: { date: today } })
      setLogs(response.data)
    } catch (error) {
      console.error('Error fetching logs:', error)
    } finally {
      setLoading(false)
    }
  }, [today])

  useEffect(() => {
    if (migrationDone) {
      fetchTodayLogs()
    }
  }, [fetchTodayLogs, migrationDone])

  const getCategoryData = (categoryId) => {
    const categoryLogs = logs.filter(log => log.category_id === categoryId)
    const totalMinutes = categoryLogs.reduce((sum, log) => sum + (log.duration_minutes || 0), 0)
    const lastNote = categoryLogs.length > 0 ? categoryLogs[0].note : null
    const hasLogs = categoryLogs.length > 0
    
    return { totalMinutes, lastNote, hasLogs, count: categoryLogs.length }
  }

  // Only count logs that have a valid category_id (ignore legacy logs without category)
  const totalMinutesToday = logs
    .filter(log => log.category_id)
    .reduce((sum, log) => sum + (log.duration_minutes || 0), 0)
  const dailyGoalMinutes = (user?.daily_goal || 2) * 60 // Convert hours to minutes (default 2 hours)
  const progressPercent = Math.min((totalMinutesToday / dailyGoalMinutes) * 100, 100)

  const handleCategoryClick = (category) => {
    // Always create a new entry - don't auto-edit existing
    setSelectedCategory(category)
    setEditingLog(null) // Always start fresh for new entry
    setShowModal(true)
  }
  
  const handleEditLog = (log, category) => {
    // Explicit edit mode - when user wants to edit a specific log
    setSelectedCategory(category)
    setEditingLog(log)
    setShowModal(true)
  }

  const fetchCategoryHistory = async (category, filter = 'all') => {
    setLoadingHistory(true)
    try {
      // Calculate date range based on filter
      let startDate = null
      const today = getTodayDate()
      
      if (filter === 'today') {
        startDate = today
      } else if (filter === 'week') {
        const d = new Date()
        d.setDate(d.getDate() - 7)
        startDate = d.toISOString().split('T')[0]
      } else if (filter === 'month') {
        const d = new Date()
        d.setDate(d.getDate() - 30)
        startDate = d.toISOString().split('T')[0]
      }
      
      const params = { category_id: category.id }
      if (startDate) {
        params.start_date = startDate
        params.end_date = today
      }
      
      const response = await axios.get(`${API}/logs`, { params })
      setCategoryHistory(response.data.slice(0, 50))
    } catch (error) {
      console.error('Error fetching history:', error)
    } finally {
      setLoadingHistory(false)
    }
  }

  const handleShowHistory = async (category) => {
    setHistoryCategory(category)
    setHistoryFilter('all')
    setShowHistory(true)
    fetchCategoryHistory(category, 'all')
  }
  
  const handleHistoryFilterChange = (filter) => {
    setHistoryFilter(filter)
    if (historyCategory) {
      fetchCategoryHistory(historyCategory, filter)
    }
  }

  const handleSearch = async () => {
    if (!searchQuery.trim()) return
    try {
      const response = await axios.get(`${API}/logs`, { 
        params: { search: searchQuery } 
      })
      setCategoryHistory(response.data)
      setHistoryCategory({ name: `Búsqueda: "${searchQuery}"` })
      setShowHistory(true)
    } catch (error) {
      console.error('Error searching:', error)
    }
  }

  const ModalWrapper = isDesktop ? Dialog : Drawer
  const ModalContent = isDesktop ? DialogContent : DrawerContent
  const ModalHeader = isDesktop ? DialogHeader : DrawerHeader
  const ModalTitle = isDesktop ? DialogTitle : DrawerTitle

  if (categoriesLoading) {
    return (
      <div className="p-4 md:p-8 pb-safe flex items-center justify-center min-h-[50vh]">
        <div className="animate-pulse text-muted-foreground">{t('today.loading')}</div>
      </div>
    )
  }

  return (
    <div className="p-4 md:p-8 pb-safe max-w-5xl mx-auto">
      {/* Header with safe area */}
      <div className="mb-6">
        <p className="text-muted-foreground text-sm font-medium mb-1">
          {formatDate(new Date(), { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
        <h1 className="text-2xl md:text-3xl font-bold mb-1">
          {locale === 'es-MX' ? 'Hola' : 'Hello'}, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-muted-foreground text-sm">
          {locale === 'es-MX' ? 'Registra tu progreso de hoy' : 'Log your progress for today'}
        </p>
      </div>

      {/* Daily Progress */}
      <div className="mb-6 p-6 rounded-2xl bg-zinc-900 dark:bg-zinc-800 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-zinc-400 text-sm font-medium">{t('today.todaysProgress')}</p>
            <p className="text-3xl font-bold font-mono">{formatDuration(totalMinutesToday)}<span className="text-lg text-zinc-400">/{formatDuration(dailyGoalMinutes)}</span></p>
          </div>
          <div className="text-right">
            <p className="text-zinc-400 text-sm">{t('settings.dailyGoal')}</p>
            <p className="text-lg font-semibold">{Math.round(progressPercent)}%</p>
          </div>
        </div>
        <Progress value={progressPercent} className="h-3 bg-zinc-700" data-testid="daily-progress-bar" />
      </div>

      {/* Kaizen Widget - Plan del día */}
      <div className="mb-6">
        <KaizenWidget />
      </div>

      {/* Bonsai Motivation Card */}
      <div className="mb-6">
        <BonsaiCard />
      </div>

      {/* Reminders Widget - Próximos 7 días */}
      <div className="mb-6">
        <RemindersWidget />
      </div>

      {/* Search */}
      <div className="mb-6 flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('today.searchNotes')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <Button onClick={handleSearch} variant="secondary" data-testid="search-btn">
          {t('today.search')}
        </Button>
      </div>

      {/* Category Grid */}
      {activeCategories.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">{t('today.noCategories')}</p>
          <p className="text-sm text-muted-foreground">{t('today.createFirst')}</p>
        </div>
      ) : (
        <div className="category-grid" data-testid="category-grid">
          {activeCategories.map((category) => {
            const { totalMinutes, lastNote, hasLogs } = getCategoryData(category.id)
            const Icon = iconMap[category.icon] || Circle
            
            return (
              <div
                key={category.id}
                className="category-card cursor-pointer group relative"
                data-testid={`category-card-${category.id}`}
              >
                <div 
                  className="h-40 md:h-48 p-4 rounded-2xl flex flex-col justify-between relative overflow-hidden"
                  style={{ backgroundColor: category.color }}
                  onClick={() => handleCategoryClick(category)}
                >
                  {/* Background icon */}
                  <Icon className="absolute -right-4 -bottom-4 w-32 h-32 text-white/10" />
                  
                  {/* Status indicator */}
                  {hasLogs && (
                    <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="relative z-10">
                    <Icon className="w-8 h-8 text-white mb-2" />
                    <h3 className="text-white font-bold text-base leading-tight">{category.name}</h3>
                  </div>
                  
                  <div className="relative z-10">
                    <p className="text-white/80 text-xs mb-1 truncate">
                      {lastNote || (locale === 'es-MX' ? 'Sin notas hoy' : 'No notes today')}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-baseline gap-1">
                        <span className="text-white font-bold text-xl font-mono">{formatDuration(totalMinutes)}</span>
                        {getCategoryData(category.id).count > 1 && (
                          <span className="text-white/60 text-xs">
                            ({getCategoryData(category.id).count})
                          </span>
                        )}
                      </div>
                      <span className="text-white/70 text-xs">{locale === 'es-MX' ? 'hoy' : 'today'}</span>
                    </div>
                  </div>
                </div>
                
                {/* History button - Pill style, positioned outside colored div */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleShowHistory(category)
                  }}
                  className="absolute bottom-4 right-4 z-30 flex items-center gap-1.5 px-3 py-1.5 min-h-[44px] rounded-full bg-black/30 hover:bg-black/40 backdrop-blur-sm border border-white/20 transition-all active:scale-95 shadow-lg"
                  data-testid={`history-btn-${category.id}`}
                >
                  <List className="w-4 h-4 text-white" />
                  <span className="text-white text-xs font-medium">
                    {locale === 'es-MX' ? 'Historial' : 'History'}
                  </span>
                </button>
              </div>
            )
          })}
        </div>
      )}

      {/* Log Modal */}
      <ModalWrapper open={showModal} onOpenChange={setShowModal}>
        <ModalContent className={isDesktop ? "sm:max-w-md" : ""}>
          <ModalHeader>
            <ModalTitle>
              {editingLog ? `Editar - ${selectedCategory?.name}` : `Nuevo registro - ${selectedCategory?.name}`}
            </ModalTitle>
          </ModalHeader>
          <LogForm 
            category={selectedCategory}
            categories={activeCategories}
            existingLog={editingLog}
            onSuccess={(streakInfo) => {
              setShowModal(false)
              setEditingLog(null)
              fetchTodayLogs()
              
              // Show celebration if it's the first log of today
              if (streakInfo?.first_log_today) {
                setCelebrationStreak(streakInfo.current_streak)
                setShowStreakCelebration(true)
              }
            }}
            onClose={() => {
              setShowModal(false)
              setEditingLog(null)
            }}
            onCategoriesRefresh={fetchCategories}
          />
        </ModalContent>
      </ModalWrapper>

      {/* Streak Celebration Overlay */}
      <StreakCelebrationOverlay 
        open={showStreakCelebration}
        currentStreak={celebrationStreak}
        onClose={() => setShowStreakCelebration(false)}
      />

      {/* History Modal */}
      <ModalWrapper open={showHistory} onOpenChange={setShowHistory}>
        <ModalContent className={isDesktop ? "sm:max-w-lg" : ""}>
          <ModalHeader>
            <ModalTitle className="flex items-center gap-2">
              <List className="w-5 h-5" />
              {locale === 'es-MX' ? 'Historial de' : 'History of'} {historyCategory?.name}
            </ModalTitle>
          </ModalHeader>
          
          {/* Filter tabs */}
          <div className="px-4 pb-2">
            <div className="flex gap-1 p-1 bg-secondary/50 rounded-lg">
              {[
                { value: 'today', label: locale === 'es-MX' ? 'Hoy' : 'Today' },
                { value: 'week', label: locale === 'es-MX' ? 'Semana' : 'Week' },
                { value: 'month', label: locale === 'es-MX' ? 'Mes' : 'Month' },
                { value: 'all', label: locale === 'es-MX' ? 'Todo' : 'All' },
              ].map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => handleHistoryFilterChange(filter.value)}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                    historyFilter === filter.value
                      ? 'bg-background shadow-sm text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`filter-${filter.value}`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* History list */}
          <div className="max-h-96 overflow-y-auto space-y-3 p-4">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-pulse text-muted-foreground">
                  {locale === 'es-MX' ? 'Cargando...' : 'Loading...'}
                </div>
              </div>
            ) : categoryHistory.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                {locale === 'es-MX' ? 'No hay registros en este periodo' : 'No records in this period'}
              </p>
            ) : (
              <>
                {/* Summary */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-primary/5 border border-primary/10 mb-2">
                  <span className="text-sm text-muted-foreground">
                    {categoryHistory.length} {locale === 'es-MX' ? 'registros' : 'records'}
                  </span>
                  <span className="text-sm font-medium" style={{ color: historyCategory?.color }}>
                    {formatDuration(categoryHistory.reduce((acc, log) => acc + (log.duration_minutes || 0), 0))} {locale === 'es-MX' ? 'total' : 'total'}
                  </span>
                </div>
                
                {categoryHistory.map((log) => (
                  <div 
                    key={log.id} 
                    className="p-4 rounded-xl bg-secondary/50 group"
                    data-testid={`history-log-${log.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex flex-col">
                        <span className="text-sm text-muted-foreground">{formatDate(log.date)}</span>
                        {log.created_at && (
                          <span className="text-xs text-muted-foreground/60">
                            {new Date(log.created_at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span 
                          className="point-badge text-white px-2.5 py-1 rounded-full text-sm font-medium"
                          style={{ backgroundColor: log.category_color || '#6b7280' }}
                        >
                          {formatDuration(log.duration_minutes || 0)}
                        </span>
                        {/* Edit button */}
                        <button
                          onClick={() => {
                            setShowHistory(false)
                            handleEditLog(log, historyCategory)
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-secondary transition-opacity"
                          title={locale === 'es-MX' ? 'Editar' : 'Edit'}
                        >
                          <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                        </button>
                      </div>
                    </div>
                    {/* Note or Subcategory as main text */}
                    <p 
                      style={{ 
                        fontSize: '16px', 
                        lineHeight: '1.35', 
                        fontWeight: '500',
                        color: 'rgba(0, 0, 0, 0.82)',
                        marginTop: '4px',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word'
                      }}
                    >
                      {log.note || log.subcategory_name || '—'}
                    </p>
                    {/* Show subcategory only if there's also a note */}
                    {log.note && log.subcategory_name && (
                      <span 
                        style={{
                          fontSize: '12px',
                          color: 'hsl(var(--muted-foreground))',
                          marginTop: '6px',
                          display: 'block'
                        }}
                      >
                        {log.subcategory_name}
                      </span>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </ModalContent>
      </ModalWrapper>
    </div>
  )
}

function LogForm({ category, categories, existingLog, onSuccess, onClose, onCategoriesRefresh }) {
  const { locale } = useLanguage()
  const isSpanish = locale === 'es-MX'
  
  // Parse existing duration or default to 15 minutes
  const existingDuration = existingLog?.duration_minutes || (existingLog?.points ? existingLog.points * 15 : 15)
  const existingHours = Math.floor(existingDuration / 60)
  const existingMins = existingDuration % 60
  
  const [formData, setFormData] = useState({
    date: getTodayDate(),
    category_id: category?.id || '',
    subcategory_id: existingLog?.subcategory_id || '',
    hours: existingHours,
    minutes: existingMins,
    note: existingLog?.note || ''
  })
  const [loading, setLoading] = useState(false)
  const [subcategories, setSubcategories] = useState([])
  const [loadingSubcategories, setLoadingSubcategories] = useState(false)

  // Fetch subcategories when category changes
  useEffect(() => {
    const fetchSubcategories = async () => {
      if (!formData.category_id) {
        setSubcategories([])
        return
      }
      
      setLoadingSubcategories(true)
      try {
        const response = await axios.get(`${API}/mejora-subcategories`, {
          params: { category_id: formData.category_id }
        })
        setSubcategories(response.data)
      } catch (error) {
        console.error('Error fetching subcategories:', error)
      } finally {
        setLoadingSubcategories(false)
      }
    }
    
    fetchSubcategories()
  }, [formData.category_id])

  useEffect(() => {
    if (category) {
      const dur = existingLog?.duration_minutes || (existingLog?.points ? existingLog.points * 15 : 15)
      setFormData(prev => ({ 
        ...prev, 
        category_id: category.id,
        subcategory_id: existingLog?.subcategory_id || '',
        hours: Math.floor(dur / 60),
        minutes: dur % 60,
        note: existingLog?.note || ''
      }))
    }
  }, [category, existingLog])

  // Apply default duration when subcategory is selected
  const handleSubcategoryChange = (subcatId) => {
    setFormData(prev => ({ ...prev, subcategory_id: subcatId }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.category_id) {
      toast.error(isSpanish ? 'Selecciona una categoría' : 'Select a category')
      return
    }
    
    const totalMinutes = (formData.hours || 0) * 60 + (formData.minutes || 0)
    
    if (totalMinutes < 1) {
      toast.error(isSpanish ? 'La duración debe ser mayor a 0' : 'Duration must be greater than 0')
      return
    }
    
    setLoading(true)

    try {
      const payload = {
        date: formData.date,
        category_id: formData.category_id,
        subcategory_id: formData.subcategory_id || null,
        duration_minutes: totalMinutes,
        note: formData.note
      }
      
      if (existingLog) {
        await axios.delete(`${API}/logs/${existingLog.id}`)
      }
      const response = await axios.post(`${API}/logs`, payload)
      toast.success(existingLog 
        ? (isSpanish ? 'Registro actualizado' : 'Entry updated')
        : (isSpanish ? 'Registro guardado' : 'Entry saved'))
      
      window.dispatchEvent(new CustomEvent('bonsai-update'))
      onSuccess(response.data?.streak)
    } catch (error) {
      toast.error(isSpanish ? 'Error al guardar' : 'Error saving')
    } finally {
      setLoading(false)
    }
  }

  // Duration presets
  const durationPresets = [
    { label: '15m', hours: 0, minutes: 15 },
    { label: '30m', hours: 0, minutes: 30 },
    { label: '1h', hours: 1, minutes: 0 },
    { label: '1h 30m', hours: 1, minutes: 30 },
    { label: '2h', hours: 2, minutes: 0 },
  ]
  
  const selectedCat = categories.find(c => c.id === formData.category_id)
  const totalMinutes = (formData.hours || 0) * 60 + (formData.minutes || 0)

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-4">
      <div className="space-y-2">
        <Label>Fecha</Label>
        <Input
          type="date"
          value={formData.date}
          onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
          data-testid="log-date-input"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>{isSpanish ? 'Categoría' : 'Category'}</Label>
          <QuickAddMejoraCategoryButton 
            onCategoryCreated={onCategoriesRefresh}
          />
        </div>
        <Select 
          value={formData.category_id} 
          onValueChange={(value) => setFormData(prev => ({ ...prev, category_id: value, subcategory_id: '' }))}
        >
          <SelectTrigger data-testid="log-category-select">
            <SelectValue placeholder={isSpanish ? 'Selecciona categoría' : 'Select category'} />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: cat.color }}
                  />
                  {cat.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Subcategory selector */}
      {formData.category_id && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>{isSpanish ? 'Subcategoría (opcional)' : 'Subcategory (optional)'}</Label>
            <QuickAddMejoraSubcategoryButton
              categoryId={formData.category_id}
              categoryName={selectedCat?.name}
              onSubcategoryCreated={() => {
                // Refetch subcategories for this category
                const fetchSubcats = async () => {
                  try {
                    const response = await axios.get(`${API}/mejora-subcategories`, {
                      params: { category_id: formData.category_id }
                    })
                    setSubcategories(response.data)
                  } catch (error) {
                    console.error('Error fetching subcategories:', error)
                  }
                }
                fetchSubcats()
              }}
            />
          </div>
          {loadingSubcategories ? (
            <div className="text-sm text-muted-foreground py-2">
              {isSpanish ? 'Cargando...' : 'Loading...'}
            </div>
          ) : subcategories.length === 0 ? (
            <div className="text-sm text-muted-foreground py-2">
              {isSpanish ? 'No hay subcategorías. Crea una usando el botón + arriba.' : 'No subcategories. Create one using the + button above.'}
            </div>
          ) : (
            <Select 
              value={formData.subcategory_id || "none"} 
              onValueChange={(value) => handleSubcategoryChange(value === "none" ? "" : value)}
            >
              <SelectTrigger data-testid="log-subcategory-select">
                <SelectValue placeholder={isSpanish ? 'Sin subcategoría' : 'No subcategory'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{isSpanish ? 'Sin subcategoría' : 'No subcategory'}</SelectItem>
                {subcategories.map((subcat) => (
                  <SelectItem key={subcat.id} value={subcat.id}>
                    {subcat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {/* Duration input */}
      <div className="space-y-3">
        <Label>{isSpanish ? 'Duración' : 'Duration'}</Label>
        
        {/* Hours and Minutes inputs */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <Input
              type="number"
              min={0}
              max={8}
              value={formData.hours}
              onChange={(e) => setFormData(prev => ({ ...prev, hours: Math.min(8, Math.max(0, parseInt(e.target.value) || 0)) }))}
              className="w-14 text-center text-lg font-bold"
              data-testid="log-hours-input"
            />
            <span className="text-sm text-muted-foreground">h</span>
          </div>
          <div className="flex items-center gap-1">
            <Input
              type="number"
              min={0}
              max={59}
              value={formData.minutes}
              onChange={(e) => setFormData(prev => ({ ...prev, minutes: Math.min(59, Math.max(0, parseInt(e.target.value) || 0)) }))}
              className="w-14 text-center text-lg font-bold"
              data-testid="log-minutes-input"
            />
            <span className="text-sm text-muted-foreground">m</span>
          </div>
        </div>
        
        {/* Quick duration presets */}
        <div className="flex flex-wrap gap-2">
          {durationPresets.map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, hours: preset.hours, minutes: preset.minutes }))}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                formData.hours === preset.hours && formData.minutes === preset.minutes
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary hover:bg-secondary/80'
              }`}
              data-testid={`quick-duration-${preset.label}`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>{isSpanish ? 'Nota / Comentario' : 'Note / Comment'}</Label>
        <Textarea
          placeholder={isSpanish ? '¿Qué hiciste hoy?' : 'What did you do today?'}
          value={formData.note}
          onChange={(e) => setFormData(prev => ({ ...prev, note: e.target.value }))}
          rows={3}
          data-testid="log-note-input"
        />
      </div>

      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          className="flex-1 rounded-full"
          onClick={onClose}
        >
          {isSpanish ? 'Cancelar' : 'Cancel'}
        </Button>
        <Button
          type="submit"
          className="flex-1 rounded-full"
          disabled={loading || totalMinutes < 1}
          style={{ backgroundColor: selectedCat?.color }}
          data-testid="log-submit-btn"
        >
          {loading 
            ? (isSpanish ? 'Guardando...' : 'Saving...') 
            : (existingLog 
                ? (isSpanish ? 'Actualizar' : 'Update')
                : (isSpanish ? 'Guardar' : 'Save'))}
        </Button>
      </div>
    </form>
  )
}
